import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Link as MuiLink,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Stepper,
  Step,
  StepLabel,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../../assets/images/logo.png";
import useAdd from "../../../api/useAdd";
import { styled as muiStyled } from "@mui/material/styles";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DeleteIcon from "@mui/icons-material/Delete";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

const AuthWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#fff",
  overflow: "hidden",
}));

const AuthCard = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  margin: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: -200,
    right: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -200,
    left: -200,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    zIndex: 0,
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ContentBox = styled(Box)({
  width: "100%",
  position: "relative",
  zIndex: 1,
  backgroundColor: "#fff",
  borderRadius: 24,
  padding: "32px 24px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 24,
  "& img": {
    height: 40,
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 16,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const BackToHomeLink = styled(Link)(({ theme }) => ({
  position: "absolute",
  top: 20,
  left: 20,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textDecoration: "none",
  fontSize: "0.9rem",
  padding: "8px 16px",
  borderRadius: 20,
  transition: "all 0.2s ease",
  zIndex: 2,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.text.primary,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

  "&:hover": {
    backgroundColor: "#fff",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const ArrowIcon = styled("span")({
  display: "inline-block",
  marginRight: "4px",
  transition: "transform 0.2s ease",
  "& svg": {
    fontSize: "1.2rem",
  },
});

const PASSWORD_REQUIREMENTS = [
  { rule: (v) => v.length >= 8, message: "length" },
  { rule: (v) => /[A-Z]/.test(v), message: "uppercase" },
  { rule: (v) => /[a-z]/.test(v), message: "lowercase" },
  { rule: (v) => /[0-9]/.test(v), message: "number" },
  {
    rule: (v) => /[^A-Za-z0-9]/.test(v),
    message: "special",
  },
];

const ImageUploadContainer = muiStyled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const PreviewContainer = muiStyled(Box)(({ theme }) => ({
  position: "relative",
  width: 150,
  height: 150,
  borderRadius: "50%",
  overflow: "hidden",
  border: `2px dashed ${theme.palette.primary.main}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.grey[100],
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.grey[200],
  },
}));

const StyledAvatar = muiStyled(Avatar)(({ theme }) => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
}));

const DeleteButton = muiStyled(Button)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  minWidth: "auto",
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
}));

const UploadIcon = muiStyled(AddAPhotoIcon)(({ theme }) => ({
  fontSize: 40,
  color: theme.palette.text.secondary,
}));

const CropDialogContainer = muiStyled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
}));

const CropDialog = muiStyled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  maxWidth: "90%",
  maxHeight: "90vh",
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const CropContainer = muiStyled(Box)({
  position: "relative",
  width: "100%",
  maxWidth: "500px",
  "& .ReactCrop": {
    maxWidth: "100%",
    "& img": {
      maxHeight: "50vh",
    },
  },
});

const ButtonGroup = muiStyled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "flex-end",
  marginTop: theme.spacing(2),
}));

const Register = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const theme = useTheme();
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    userType: "is_reader",
    bio: "",
    profilePicture: null,
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [cropDialog, setCropDialog] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = React.useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    handleAdd: handleRegister,
    loading: registerLoading,
    isSuccess,
    SnackbarMessage,
  } = useAdd(
    "register",
    null,
    "Registration successful! Please check your email to verify your account.",
    "Registration failed",
  );

  const steps = [
    t('auth.register.steps.accountType'),
    t('auth.register.steps.basicInfo'),
    t('auth.register.steps.credentials')
  ];

  useEffect(() => {
    if (isSuccess) {
      resetForm();
    }
  }, [isSuccess]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resizeImage = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve(
                new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                }),
              );
            },
            "image/jpeg",
            0.8,
          );
        };
      };
    });
  }, []);

  function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );
  }

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
        setCropDialog(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height,
    );

    canvas.toBlob(
      async (blob) => {
        if (!blob) return;

        try {
          setIsResizing(true);
          const resizedImage = await resizeImage(
            new File([blob], "cropped.jpg", { type: "image/jpeg" }),
          );
          setFormData({
            ...formData,
            profilePicture: resizedImage,
          });
          setImagePreview(URL.createObjectURL(resizedImage));
        } catch (error) {
          console.error("Error processing image:", error);
        } finally {
          setIsResizing(false);
          setCropDialog(false);
        }
      },
      "image/jpeg",
      0.9,
    );
  }, [completedCrop, imgRef]);

  const handleRemoveImage = () => {
    setFormData({
      ...formData,
      profilePicture: null,
    });
    setImagePreview(null);
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.userType) {
          newErrors.userType = "Please select an account type";
        }
        break;

      case 1:
        if (!formData.firstName?.trim()) {
          newErrors.firstName = "First name is required";
        }
        if (!formData.lastName?.trim()) {
          newErrors.lastName = "Last name is required";
        }
        if (!formData.phone?.trim()) {
          newErrors.phone = "Phone number is required";
        }
        if (formData.userType === "is_author" && !formData.bio?.trim()) {
          newErrors.bio = "Bio is required for authors";
        }
        break;

      case 2:
        if (!formData.username?.trim()) {
          newErrors.username = "Username is required";
        } else if (formData.username.length < 3) {
          newErrors.username = "Username must be at least 3 characters";
        }

        if (!formData.email?.trim()) {
          newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = "Invalid email format";
        }

        if (!formData.password) {
          newErrors.password = "Password is required";
        } else {
          const passwordValidationErrors = PASSWORD_REQUIREMENTS.filter(
            (req) => !req.rule(formData.password),
          ).map((req) => req.message);
          if (passwordValidationErrors.length > 0) {
            newErrors.password = "Password requirements not met";
          }
        }

        if (!formData.confirmPassword) {
          newErrors.confirmPassword = "Please confirm your password";
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match";
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      phone: "",
      userType: "is_reader",
      bio: "",
      profilePicture: null,
    });
    setActiveStep(0);
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateStep(2)) {
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("username", formData.username);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("password", formData.password);
    formDataToSend.append("first_name", formData.firstName);
    formDataToSend.append("last_name", formData.lastName);
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append(formData.userType, true);

    if (formData.userType === "is_author") {
      formDataToSend.append("bio", formData.bio);
      if (formData.profilePicture) {
        formDataToSend.append("profile_picture", formData.profilePicture);
      }
    }

    handleRegister(formDataToSend);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderAuthorFields = () => (
    <>
      <StyledTextField
        fullWidth
        multiline
        rows={4}
        placeholder={t('auth.register.fields.bio')}
        name="bio"
        value={formData.bio}
        onChange={handleChange}
        error={!!errors.bio}
        helperText={errors.bio}
      />
      <ImageUploadContainer>
        <input
          accept="image/*"
          style={{ display: "none" }}
          id="profile-picture"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="profile-picture">
          <PreviewContainer>
            {isResizing ? (
              <CircularProgress size={40} />
            ) : imagePreview ? (
              <>
                <StyledAvatar src={imagePreview} alt="Profile preview" />
                <DeleteButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemoveImage();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </DeleteButton>
              </>
            ) : (
              <UploadIcon />
            )}
          </PreviewContainer>
        </label>
        <Typography variant="caption" color="textSecondary" align="center">
          {imagePreview
            ? t('auth.register.profilePicture.change')
            : t('auth.register.profilePicture.upload')}
        </Typography>
      </ImageUploadContainer>
    </>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <FormControl component="fieldset" sx={{ width: "100%", mt: 2 }}>
            <FormLabel component="legend">{t('auth.register.accountType.legend')}</FormLabel>
            <RadioGroup
              name="userType"
              value={formData.userType}
              onChange={handleChange}
            >
              <FormControlLabel
                value="is_reader"
                control={<Radio />}
                label={t('auth.register.accountType.reader')}
              />
              <FormControlLabel
                value="is_author"
                control={<Radio />}
                label={t('auth.register.accountType.author')}
              />
            </RadioGroup>
            {errors.userType && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.userType}
              </Alert>
            )}
          </FormControl>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.firstName')}
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.lastName')}
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.phone')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
            {formData.userType === "is_author" && renderAuthorFields()}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.username')}
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
            />
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.password')}
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 1, mb: 2 }}>
              {PASSWORD_REQUIREMENTS.map((req, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    display: "block",
                    color: req.rule(formData.password)
                      ? "success.main"
                      : "text.secondary",
                    "&::before": {
                      content: '"✓ "',
                      color: req.rule(formData.password)
                        ? "success.main"
                        : "text.disabled",
                    },
                  }}
                >
                  {t(`auth.register.passwordRequirements.${req.message}`)}
                </Typography>
              ))}
            </Box>
            <StyledTextField
              fullWidth
              placeholder={t('auth.register.fields.confirmPassword')}
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <AuthWrapper dir={isRtl ? 'rtl' : 'ltr'}>
      <BackToHomeLink to="/">
        <ArrowIcon>←</ArrowIcon>
        {t('auth.register.backToHome')}
      </BackToHomeLink>

      <AuthCard>
        <ContentBox>
          <LogoContainer>
            <img src={Logo} alt="Logo" />
          </LogoContainer>

          <Typography variant="h5" align="center" gutterBottom fontWeight={600}>
            {t('auth.register.title')}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel 
                  sx={{
                    '& .MuiStepIcon-text': {
                      fill: theme.palette.primary.contrastText,
                    },
                    ...(isRtl && {
                      '& .MuiStepLabel-labelContainer': {
                        marginRight: '8px',
                        marginLeft: '-8px'
                      },
                      '& .MuiStepIcon-root': {
                        marginLeft: '8px'
                      }
                    })
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (activeStep === steps.length - 1) {
                handleSubmit(e);
              } else {
                handleNext();
              }
            }}
          >
            {renderStepContent(activeStep)}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              {activeStep > 0 && <Button onClick={handleBack}>{t('auth.register.buttons.back')}</Button>}
              <Button
                variant="contained"
                type="submit"
                disabled={registerLoading}
              >
                {activeStep === steps.length - 1
                  ? registerLoading
                    ? t('auth.register.buttons.creating')
                    : t('auth.register.buttons.create')
                  : t('auth.register.buttons.next')}
              </Button>
            </Box>
          </form>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t('auth.register.signIn.text')}{" "}
              <MuiLink
                component={Link}
                to="/login"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                {t('auth.register.signIn.link')}
              </MuiLink>
            </Typography>
          </Box>
        </ContentBox>
      </AuthCard>
      {SnackbarMessage}

      {cropDialog && (
        <CropDialogContainer>
          <CropDialog>
            <Typography variant="h6" align="center">
              {t('auth.register.cropDialog.title')}
            </Typography>
            <CropContainer>
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  style={{ maxWidth: "100%" }}
                />
              </ReactCrop>
            </CropContainer>
            <ButtonGroup>
              <Button
                variant="outlined"
                onClick={() => {
                  setCropDialog(false);
                  setImgSrc("");
                }}
              >
                {t('auth.register.cropDialog.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleCropComplete}
                disabled={!completedCrop?.width || !completedCrop?.height}
              >
                {t('auth.register.cropDialog.apply')}
              </Button>
            </ButtonGroup>
          </CropDialog>
        </CropDialogContainer>
      )}
    </AuthWrapper>
  );
};

export default Register;
